"use client";

import type * as React from "react";
import { useId } from "react";
import { Checkbox } from "@/components/ui/atoms/checkbox";
import { cn } from "@/lib/utils";

export type McpToolSearchState = "on" | "off";

export type McpToolSearchBandProps = {
  state: McpToolSearchState;
  onToggle?: (checked: boolean) => void;
} & Omit<React.ComponentProps<"label">, "children" | "onToggle">;

/**
 * Presentational Personalized Tool Search opt-in for the MCP picker. The whole
 * card is a `<label>` for the checkbox, so a click anywhere on it toggles.
 */
export function McpToolSearchBand({
  state,
  onToggle,
  className,
  ...rest
}: McpToolSearchBandProps) {
  const active = state === "on";
  const checkboxId = useId();

  return (
    <label
      {...rest}
      className={cn(
        "flex cursor-pointer items-start gap-3 rounded-lg border px-4 py-3 transition-colors",
        active
          ? "border-foreground/20 bg-muted/30"
          : "border-border/60 bg-muted/10 hover:border-foreground/25",
        className
      )}
      data-slot="mcp-tool-search-band"
      data-status={state}
      htmlFor={checkboxId}
    >
      <Checkbox
        aria-label={`Personalized Tool Search ${active ? "on" : "off"}`}
        checked={active}
        className="mt-0.5"
        id={checkboxId}
        onCheckedChange={onToggle}
      />
      <div className="min-w-0">
        <p className="font-semibold text-[15px] text-foreground leading-5">
          Personalized Tool Search
        </p>
        <p className="mt-1 text-[13px] text-muted-foreground leading-snug">
          Your agent fetches only the tools relevant to each task, saving tokens
          and keeping responses focused.
        </p>
      </div>
    </label>
  );
}
